// app/shared/hooks/browserStorageStore.ts
"use client";

import { useSyncExternalStore } from "react";

const CHANGE_EVENT = "htf-browser-storage";

type Listener = () => void;

type BrowserStorageOptions<T> = {
  key: string;
  defaultValue: T;
  parse: (raw: string | null) => T;
  serialize: (value: T) => string;
};

function readStorage<T>(options: BrowserStorageOptions<T>): T {
  if (typeof window === "undefined") {
    return options.defaultValue;
  }
  try {
    return options.parse(window.localStorage.getItem(options.key));
  } catch {
    return options.defaultValue;
  }
}

export function createBrowserStorageStore<T>(
  options: BrowserStorageOptions<T>,
) {
  let snapshot = options.defaultValue;
  const listeners = new Set<Listener>();

  function emitFromStorage() {
    snapshot = readStorage(options);
    listeners.forEach((listener) => listener());
  }

  function subscribe(onStoreChange: Listener) {
    listeners.add(onStoreChange);
    if (typeof window === "undefined") {
      return () => {
        listeners.delete(onStoreChange);
      };
    }

    emitFromStorage();

    const onStorage = (event: StorageEvent) => {
      if (event.key === options.key || event.key === null) {
        emitFromStorage();
      }
    };
    const onLocal = (event: Event) => {
      const detail = (event as CustomEvent<{ key?: string }>).detail;
      if (!detail?.key || detail.key === options.key) {
        emitFromStorage();
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGE_EVENT, onLocal);
    return () => {
      listeners.delete(onStoreChange);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGE_EVENT, onLocal);
    };
  }

  function getSnapshot() {
    return snapshot;
  }

  function getServerSnapshot() {
    return options.defaultValue;
  }

  function set(value: T) {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(options.key, options.serialize(value));
      } catch {
        /* quota or unavailable storage */
      }
      window.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, { detail: { key: options.key } }),
      );
    }
    snapshot = value;
    listeners.forEach((listener) => listener());
  }

  function useStore() {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  }

  return {
    key: options.key,
    subscribe,
    getSnapshot,
    getServerSnapshot,
    set,
    useStore,
  };
}
